import { MergeRequest, MergeRequestBase } from '../types/MergeRequest';
import { discoveryApiRef, useApi } from '@backstage/core-plugin-api';
import { useEffect, useRef } from 'react';

import { getMergeRequests } from '../utils/getMergeRequests';
import { getPipelineCount } from '../utils/getPipelineCount';
import { identityApiRef } from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';
import { Team } from '@internal/schemas-common';

type UseGetMergeRequestsProps = { teams: Team[], useFullArrayAsDependency?: boolean; };
type UseGetMergeRequestsResult = {
  value: MergeRequest[] | undefined;
  loading: boolean;
  error: Error | undefined;
};

export const useGetMergeRequests = ({
  teams
}: UseGetMergeRequestsProps): UseGetMergeRequestsResult => {
  const proxyBaseurl = useRef('');
  const token = useRef('');
  const mergeRequests = useRef<MergeRequest[]>([]);
  const apiRef = useApi(discoveryApiRef);
  const identityRef = useApi(identityApiRef);
  const teamsIdentityKey = JSON.stringify(teams);

  useEffect(() => {
    identityRef.getCredentials().then(cred => {
      if (cred?.token) {
        token.current = cred.token;
      }
    });
  }, [identityRef, teamsIdentityKey]);

  // Get proxy URL
  useEffect(() => {
    apiRef.getBaseUrl('custom-proxy').then(baseUrl => {
      if (baseUrl) {
        proxyBaseurl.current = baseUrl;
      }
    });
  }, [apiRef, teamsIdentityKey]);

  const { value, loading, error } = useAsync(async () => {
    if (!teams?.length) {
      return [];
    }

    // Get MRs from proxy

    const baseMrs = await Promise.allSettled<MergeRequestBase[]>(
      teams.map(team =>
        getMergeRequests({
          baseUrl: proxyBaseurl.current,
          token: token.current,
          teamName: team.nameInGitlab,
        }),
      ),
    );

    const allBaseMrs = baseMrs
      .filter(baseMr => baseMr?.status === 'fulfilled')
      .map(
        baseMr => (baseMr as PromiseFulfilledResult<MergeRequestBase[]>).value,
      )
      .flat();

    // Get pipelines from proxy
    const pipelineCounts = await Promise.all(
      allBaseMrs.map(baseMr =>
        getPipelineCount({
          baseUrl: proxyBaseurl.current,
          token: token.current,
          mr: baseMr,
        }),
      ),
    );

    // add pipeline count, days count to merge request responses
    return allBaseMrs
      .map((baseMr, index) => ({
        ...baseMr,
        days_count: Math.floor(
          (new Date().getTime() - new Date(baseMr.created_at).getTime()) /
            (1000 * 60 * 60 * 24),
        ),
        pipeline_count: pipelineCounts[index],
      }))
      .filter(mr => mr.pipeline_count >= 0);
  }, [
    proxyBaseurl.current,
    token.current,
    teamsIdentityKey,
  ]);

  if (value?.length) {
    mergeRequests.current = value;
  }

  return { value: mergeRequests.current, loading, error };
};
